import { usePaymentMethodsApi } from "@/Hooks/usePaymentMethodsApi";
import BookShipmentCreditCardsDropdown from "./Dropdowns/BookShipmentCreditCardsDropdown";
import { displayNameForCarrier } from "@/Data/displayNameForCarrier";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { Spinner } from "@freightsimple/ui-core/Feedback/Spinner";
import { Panel } from "@freightsimple/ui-core/PageLayout/Panel";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import DynamicSpacer from "@/Shared/Layout/CustomComponents/DynamicSpacer";
import { isCreditCardExpired } from "@/Helpers/creditCardExpiry";
import CreditCardsCardEntryForm from "@/Shared/CreditCards/Forms/CreditCardsCardEntryForm";
import BookShipmentScreenLayout from "./CustomComponents/BookShipmentScreenLayout";
import CreditCardsAddLegalease from "@/Shared/CreditCards/CustomComponents/CreditCardsAddLegalease";
import CreditCardsStoreButton from "@/Shared/CreditCards/Buttons/CreditCardsStoreButton";
import useStoreCreditCard from "@/Features/CreditCards/Exports/Hooks/useCreditCardsStore";
import { CreditCardsShareDialog } from "@/Features/CreditCards/Exports/Dialogs/CreditCardsShareDialog";
import type { Address } from "@freightsimple/generated-dashboard-openapi-client/models/Address";
import type { CreditCard } from "@freightsimple/generated-dashboard-openapi-client/models/CreditCard";
import type { PreBookingShipment } from "@freightsimple/generated-dashboard-openapi-client/models/PreBookingShipment";
import { useOnce } from "@/Hooks/useOnce";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useShipmentService2 } from "@/Services/ShipmentService2";

interface BookShipmentPaymentScreenProps {
  shipment: PreBookingShipment;
  onComplete: () => void;
  onPrevious: () => Promise<void>;
  nextIdentifier: string;
  onBrandChange: (brandName: string) => void;
}

function BookShipmentPaymentScreen(props: BookShipmentPaymentScreenProps) {
  const { shipment, onBrandChange } = props;
  const { shipmentId } = shipment;
  const {
    storeCreditCard,
    billingAddress,
    billingName,
    setBillingName,
    setBillingAddress,
    errorMessageForBillingName,
    errorMessageForCreditCardNumber,
    errorMessageForCreditCardExpiration,
    errorMessageForCreditCardCVC,
    setErrorMessageForCreditCardNumber,
    setErrorMessageForCreditCardExpiration,
    setErrorMessageForCreditCardCVC,
    errorMessageForAddress,
    errorMessageForCity,
    errorMessageForStateOrProvinceCode,
    locationsErrorMessageForPostalCode,
    errorMessageForCountry,
    isValid,
  } = useStoreCreditCard();
  const [stripePaymentMethodId, setStripePaymentMethodId] = useState<
    string | undefined
  >();
  const navigate = useNavigate();
  const [forceValidation, setForceValidation] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | undefined>();
  const [storingInProgress, setStoringInProgress] = useState(false);
  const [storingSuccess, setStoringSuccess] = useState(false);
  const [storingFailure, setStoringFailure] = useState(false);
  const [failureMessage, setFailureMessage] = useState<string>();
  const shipmentsService = useShipmentService2();
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(true);
  const [shareCardOpen, setShareCardOpen] = useState(false);

  const carrierDisplayName = displayNameForCarrier(
    shipment.selectedQuote?.carrierIdentifier,
  );

  if (!carrierDisplayName) {
    throw new Error("Missing carrierDisplayName");
  }

  const pickupAddress = shipment.pickupLocation.address as Address;
  const deliveryAddress = shipment.deliveryLocation.address as Address;

  const paymentMethodsApi = usePaymentMethodsApi();

  const [creditCards, setCreditCards] = useState<
    undefined | Array<CreditCard>
  >();

  const [getCreditCardInterval, setGetCreditCardInterval] = useState<
    undefined | NodeJS.Timeout
  >();

  // The 30s polling interval below captures the first render's closure, so it
  // would always see stripePaymentMethodId === undefined and re-run auto-select
  // every tick — silently overriding a card the user picked manually. The ref
  // always reflects the current selection regardless of which render the
  // closure came from (ENG-4368)
  const stripePaymentMethodIdRef = useRef<string | undefined>(undefined);

  async function selectNewCard(_stripePaymentMethodId: string) {
    stripePaymentMethodIdRef.current = _stripePaymentMethodId;
    setStripePaymentMethodId(_stripePaymentMethodId);
    await shipmentsService.setStripePaymentMethodId(
      shipmentId,
      _stripePaymentMethodId,
    );
  }

  async function getCreditCards() {
    const creditCardsResponse = await paymentMethodsApi.getAllPaymentMethods();
    const creditCards = creditCardsResponse.creditCards;
    setCreditCards(creditCards);

    const numberOfCreditCards = creditCards?.length;
    setShowForm(numberOfCreditCards && numberOfCreditCards > 0 ? false : true);

    // Auto-select the first usable card — never an expired one (ENG-4368)
    const firstUsableCreditCard = creditCards?.find(
      (card) => !isCreditCardExpired(card.expiry),
    );
    if (
      stripePaymentMethodIdRef.current === undefined &&
      firstUsableCreditCard !== undefined
    ) {
      selectNewCard(firstUsableCreditCard.stripePaymentMethodId);
    }
  }

  // When we stop rendering the component, clean up the polling
  useEffect(function () {
    return function () {
      if (getCreditCardInterval !== undefined) {
        clearInterval(getCreditCardInterval);
      }
    };
  });

  useOnce(async () => {
    paymentMethodsApi.generateSaveCardIntent().then((response) => {
      setClientSecret(response.clientSecret);
    });

    await getCreditCards();
    setLoading(false);

    const interval = setInterval(getCreditCards, 30000);
    setGetCreditCardInterval(interval);
  });

  // Next (rendered only when the customer has cards) must not proceed without
  // a usable selected card: when every card is expired nothing auto-selects,
  // and stale state can leave an expired (or since-removed) card selected
  // (ENG-4368)
  const selectedCard = creditCards?.find(
    (card) => card.stripePaymentMethodId === stripePaymentMethodId,
  );
  const selectedCardIsExpired =
    selectedCard !== undefined && isCreditCardExpired(selectedCard.expiry);
  const noUsableCardSelected =
    selectedCard === undefined || selectedCardIsExpired;

  function navigateToReview() {
    props.onComplete();
    setTimeout(function () {
      navigate(`/book/${props.nextIdentifier}?shipmentId=${shipmentId}`);
    }, 1000);
  }

  async function onStoreCreditCard() {
    if (!clientSecret) {
      throw new Error("Missing clientSecret");
    }

    setForceValidation(true);

    if (!isValid) {
      return;
    }

    setStoringSuccess(false);
    setStoringFailure(false);
    setStoringInProgress(true);

    const result = await storeCreditCard(clientSecret);

    setStoringInProgress(false);

    if (result.setupIntent && result.setupIntent.paymentMethod) {
      setStoringSuccess(true);
      selectNewCard(result.setupIntent.paymentMethod);
      navigateToReview();
    } else {
      setStoringFailure(true);
      setFailureMessage(result.error?.message);
    }
  }

  // Declared above the loading gate so the dialog survives it: a screen that
  // owns an open Dialog must never unmount it, so every return path renders
  // the dialog.
  const shareCardDialog = (
    <CreditCardsShareDialog
      open={shareCardOpen}
      onOpenChange={setShareCardOpen}
    />
  );

  if (loading) {
    return (
      <>
        {shareCardDialog}
        <Stack gap={0} align="center">
          <T>Loading</T>
          <Spinner size={64} />
        </Stack>
      </>
    );
  }

  return (
    <>
      {shareCardDialog}
      <BookShipmentScreenLayout
        pageHeading={`Enter Credit Card Details`}
        pageSubheading={
          <Stack gap={2} align="start">
            <T>
              We will securely store your credit card to make it easier to pay
              your freight charges. Visa, Mastercard, and American Express are
              accepted.
            </T>
            <Button size="link" onClick={() => setShareCardOpen(true)}>
              Don&apos;t have access to the company credit card? Click here to
              invite someone else
            </Button>
          </Stack>
        }
      >
        {/* Underlined like every other step of the booking flow — set once for
         * the whole screen so the saved-card dropdown and the card entry form
         * agree. */}
        <ControlPresentationProvider presentation="underlined">
          {!showForm && (
            <Panel collapseOnNarrow>
              <Stack gap={16}>
                <Stack gap={4}>
                  {/* Replica of the legacy Cargo Microcopy (16px light text). */}
                  <T size="base" color="secondary">
                    Some credit cards have already been added to your account.
                    Please select the one you would like to use for this
                    shipment
                  </T>
                  <Stack gap={2}>
                    <BookShipmentCreditCardsDropdown
                      selectedStripePaymentMethodId={stripePaymentMethodId}
                      onOptionSelected={selectNewCard}
                      cards={creditCards}
                    />
                    <Row>
                      <Button size="link" onClick={() => setShowForm(true)}>
                        Add a different card?
                      </Button>
                    </Row>
                  </Stack>
                </Stack>
                <Row>
                  <Button
                    primary
                    data-testid="next-button"
                    size="large"
                    onClick={navigateToReview}
                    disabled={noUsableCardSelected}
                    disabledMessage={
                      selectedCardIsExpired
                        ? "The selected credit card has expired. Please select a different card or add a new one."
                        : "Select a credit card to continue, or add a new one."
                    }
                  >
                    Next
                  </Button>
                </Row>
              </Stack>
            </Panel>
          )}
          {showForm && (
            <Panel collapseOnNarrow>
              <CreditCardsCardEntryForm
                addresses={[pickupAddress, deliveryAddress]}
                billingAddress={billingAddress}
                billingName={billingName}
                setBillingAddress={setBillingAddress}
                setBillingName={setBillingName}
                forceValidation={forceValidation}
                onBrandChange={onBrandChange}
                errorMessageForBillingName={errorMessageForBillingName}
                errorMessageForCreditCardNumber={
                  errorMessageForCreditCardNumber
                }
                errorMessageForCreditCardExpiration={
                  errorMessageForCreditCardExpiration
                }
                errorMessageForCreditCardCVC={errorMessageForCreditCardCVC}
                errorMessageForAddress={errorMessageForAddress}
                errorMessageForCity={errorMessageForCity}
                errorMessageForStateOrProvinceCode={
                  errorMessageForStateOrProvinceCode
                }
                locationsErrorMessageForPostalCode={
                  locationsErrorMessageForPostalCode
                }
                errorMessageForCountry={errorMessageForCountry}
                onCreditCardCVCError={setErrorMessageForCreditCardCVC}
                onCreditCardExpirationError={
                  setErrorMessageForCreditCardExpiration
                }
                onCreditCardNumberError={setErrorMessageForCreditCardNumber}
                onValidate={() => setForceValidation(true)}
              />

              {/* More breathing room on tall viewports, compact on short
               * ones. */}
              <DynamicSpacer minHeight={8} maxHeight={32} />
              <Stack gap={2}>
                <CreditCardsStoreButton
                  onClick={onStoreCreditCard}
                  storingInProgress={storingInProgress}
                  storingSuccess={storingSuccess}
                  storingFailure={storingFailure}
                  failureMessage={failureMessage}
                />
                <CreditCardsAddLegalease />
              </Stack>
            </Panel>
          )}
        </ControlPresentationProvider>
      </BookShipmentScreenLayout>
    </>
  );
}

export default BookShipmentPaymentScreen;
